import { createSelector, createSlice } from "@reduxjs/toolkit";

export interface JwtPayload {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  exp?: number;
  nbf?: number;
  iat?: number;
  jti?: string;
}

export interface AppJwtPayload extends JwtPayload {
  business_units?: number[];
  business_unit_platform_id?: string;
  business_unit_platform_env_id?: string;
  email: string;
  user_id: number;
  roles?: string[];
  name?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  tokenDecode: AppJwtPayload | null;
  token: string | null;
}

type StateWithAuth = { auth: AuthState };

function decodeJwt(token: string): AppJwtPayload | null {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload)) as AppJwtPayload;
  } catch {
    return null;
  }
}

const initialState: AuthState = {
  isAuthenticated: false,
  tokenDecode: null,
  token: null,
};

const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken(state, { payload }: { payload: string }) {
      const decoded = decodeJwt(payload);
      state.token = payload;
      state.tokenDecode = decoded;
      state.isAuthenticated = !!decoded;
    },
    clear(state) {
      state.token = null;
      state.tokenDecode = null;
      state.isAuthenticated = false;
    },
  },
});

export const { setToken, clear } = AuthSlice.actions;
export default AuthSlice.reducer;

// Selectors
export const selectToken = (state: StateWithAuth) => state.auth.token;
export const selectIsAuthenticated = (state: StateWithAuth) =>
  state.auth.isAuthenticated;
export const selectTokenDecode = (state: StateWithAuth) =>
  state.auth.tokenDecode;

export const selectIsTokenExpired = (state: StateWithAuth): boolean => {
  const tokenDecode = state.auth.tokenDecode;
  if (!tokenDecode?.exp) return true;
  return Date.now() / 1000 > tokenDecode.exp;
};

export const selectUser = createSelector(
  [selectTokenDecode],
  (tokenDecode) =>
    tokenDecode
      ? {
          id: tokenDecode.user_id,
          email: tokenDecode.email,
          name: tokenDecode.name,
          roles: tokenDecode.roles,
        }
      : null
);
